import { useCallback, useEffect, useState } from 'react';
import { getDataFromLocalStorage } from 'common/useStoredState';
import { LocalStorageKeys } from '@/constants/localStorageKeys';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useTranslation } from 'react-i18next';
import {
  useGetOrganizationAdditionalInfoQuery,
  useUpdateGettingStartedProgressMutation,
} from '@/redux/slices/organizationApiSlice';
import { SessionStorageItems } from '@/types/sessionStorageItems';
import type { GettingStartedProgress } from '@/types/aiChat';

export type { GettingStartedProgress };

// Tracks which orgs have already had their legacy localStorage progress
// migrated in this session, so simultaneously-mounted consumers of
// useGettingStartedStorage don't each fire a duplicate migration write.
const migratedOrganizationIds = new Set<string>();

/**
 * Lightweight hook exposing just the progress-update function, without also
 * subscribing to getOrganizationAdditionalInfo. Use this in places (e.g. the
 * AI setup assistant, mounted app-wide) that only need to write progress and
 * don't render from it — avoids firing an extra network request on every page.
 */
export const useUpdateGettingStartedProgress = () => {
  const organizationId = useGetOrganizationId();
  const { showErrorToast } = useGlobalUI();
  const { t } = useTranslation('getting-started');

  const [updateGettingStartedProgress] =
    useUpdateGettingStartedProgressMutation();

  return useCallback(
    (updates: Partial<GettingStartedProgress>) => {
      updateGettingStartedProgress({
        organizationId,
        gettingStartedProgress: updates,
      })
        .unwrap()
        .catch(() => {
          showErrorToast({
            id: 'getting-started-update-progress-error',
            message: t('container.updateProgressError'),
            stack: true,
          });
        });
    },
    [organizationId, updateGettingStartedProgress, showErrorToast, t]
  );
};

/**
 * Custom hook for managing getting started guide visibility and completion tracking.
 * Progress is persisted server-side via OrganizationAdditionalInfo (synced across
 * browsers/devices for the same org); guide visibility for the current session uses
 * sessionStorage.
 *
 * Guide visibility logic:
 * - Show guide: If not completed OR if this is the session where it was completed
 * - Hide guide: If completed in a previous session
 *
 * @returns Object containing current progress state, update functions, and visibility logic
 */
export const useGettingStartedStorage = () => {
  const organizationId = useGetOrganizationId();

  const startedSessionWithIncompleteGuide =
    `${SessionStorageItems.STARTED_SESSION_WITH_INCOMPLETE_GUIDE}_${organizationId}` as const;

  const { data } = useGetOrganizationAdditionalInfoQuery({ organizationId });
  const progress = data?.gettingStartedProgress ?? {};

  const updateProgress = useUpdateGettingStartedProgress();

  const [shouldShowGuide, setShouldShowGuide] = useState<boolean>(
    sessionStorage.getItem(startedSessionWithIncompleteGuide) === 'true'
  );

  // One-time migration for orgs with progress recorded in the old
  // localStorage-backed store (pre-server-persistence). Runs once data has
  // loaded; only migrates legacy keys the server doesn't already have, so a
  // server write that only covers some keys (e.g. a single completed step)
  // doesn't permanently block migration of the rest. Guarded by a
  // module-level set (rather than a component-level ref) since multiple
  // components mount this hook simultaneously and would otherwise each fire
  // their own duplicate migration write.
  useEffect(() => {
    if (
      !organizationId ||
      data === undefined ||
      migratedOrganizationIds.has(organizationId)
    ) {
      return;
    }
    migratedOrganizationIds.add(organizationId);

    const legacyProgress = getDataFromLocalStorage(
      `${LocalStorageKeys.GETTING_STARTED_PROGRESS}_${organizationId}`
    ) as GettingStartedProgress | null;

    if (!legacyProgress) {
      return;
    }

    const serverProgress = data.gettingStartedProgress ?? {};
    const missingLegacyProgress = Object.fromEntries(
      Object.entries(legacyProgress).filter(([key]) => !(key in serverProgress))
    ) as Partial<GettingStartedProgress>;

    if (Object.keys(missingLegacyProgress).length > 0) {
      updateProgress(missingLegacyProgress);
    }
  }, [organizationId, data, updateProgress]);

  const setStartedSessionWithIncompleteGuide = useCallback(() => {
    sessionStorage.setItem(startedSessionWithIncompleteGuide, 'true');
    setShouldShowGuide(true);
  }, [startedSessionWithIncompleteGuide]);

  // Clears the session flag from sessionStorage so the guide won't reappear on
  // the next page load. Does NOT update React state — the guide stays visible
  // for the remainder of this render cycle (until the page actually reloads).
  const clearStartedSessionWithIncompleteGuide = useCallback(() => {
    sessionStorage.removeItem(startedSessionWithIncompleteGuide);
  }, [startedSessionWithIncompleteGuide]);

  // Check if the guide should be visible based on completion and session state
  const shouldShowGettingStartedGuide = useCallback(() => {
    return shouldShowGuide;
  }, [shouldShowGuide]);

  return {
    // Current progress state (raw object)
    progress,

    // Update functions
    updateProgress,

    // Completion and visibility tracking
    setStartedSessionWithIncompleteGuide,
    clearStartedSessionWithIncompleteGuide,
    shouldShowGettingStartedGuide,

    // Convenience boolean properties with defaults
    skipAbm: progress.skipAbm ?? false,
    skipMacMdm: progress.skipMacMdm ?? false,
    skipItChampions: progress.skipItChampions ?? false,
    skipHr: progress.skipHr ?? false,
    startMacMdm: progress.startMacMdm ?? false,
    skipGroups: progress.skipGroups ?? false,
    skipIntegrations: progress.skipIntegrations ?? false,
    skipPaymentMethod: progress.skipPaymentMethod ?? false,
    notifyTeam: progress.notifyTeam ?? false,
    ztpConfigured: progress.ztpConfigured ?? false,
    inviteEmployees: progress.inviteEmployees ?? false,
  };
};
